import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const STATUS = ["new", "contacted", "closed"];

export default function EnquiriesAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/enquiries").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (e, status) => {
    try { const r = await api.patch(`/admin/enquiries/${e.id}`, { status }); setItems((s) => s.map((x) => (x.id === e.id ? r.data : x))); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (e) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/admin/enquiries/${e.id}`); setItems((s) => s.filter((x) => x.id !== e.id));
  };

  return (
    <div data-testid="admin-enquiries">
      <p className="eyebrow text-[var(--terracotta)]">Enquiries</p>
      <h1 className="font-display text-4xl mt-2 text-[var(--ink)]">{items.filter((i) => i.status === "new").length} new · {items.length} total</h1>
      <p className="text-sm text-[var(--muted)] mt-2">Email notifications are not configured yet — enquiries are captured here.</p>
      <div className="mt-8 space-y-3">
        {items.map((e) => (
          <div key={e.id} className="bg-white border border-[var(--sand)] rounded-lg" data-testid={`enquiry-row-${e.id}`}>
            <button type="button" onClick={() => setOpen(open === e.id ? null : e.id)} className="w-full text-left p-4 flex flex-wrap items-center gap-4">
              <span className={`eyebrow !text-[0.6rem] px-2 py-1 rounded-full ${e.status === "new" ? "bg-[var(--terracotta)] text-[var(--cream)]" : e.status === "contacted" ? "bg-[var(--sand)] text-[var(--ink)]" : "bg-[#eee] text-[var(--muted)]"}`}>{e.status}</span>
              <span className="font-medium text-[var(--ink)]">{e.company}</span>
              <span className="text-sm text-[var(--charcoal)]">{e.name} · {e.email}</span>
              {e.brand_slug && <span className="text-xs text-[var(--terracotta)]">re: {e.brand_slug}</span>}
              {e.notified_at && <span className="text-[0.65rem] uppercase tracking-wider text-green-700" title={`Emailed ${new Date(e.notified_at).toLocaleString("en-AU")}`} data-testid={`enquiry-emailed-${e.id}`}>✓ emailed</span>}
              {e.notify_error && <span className="text-[0.65rem] uppercase tracking-wider text-red-600" title={e.notify_error} data-testid={`enquiry-email-failed-${e.id}`}>email failed</span>}
              <span className="ml-auto text-xs text-[var(--muted)]">{new Date(e.created_at).toLocaleString("en-AU")}</span>
            </button>
            {open === e.id && (
              <div className="px-4 pb-4 border-t border-[var(--sand)] pt-4 text-sm grid md:grid-cols-2 gap-4" data-testid={`enquiry-detail-${e.id}`}>
                <div className="space-y-1 text-[var(--charcoal)]">
                  <p><span className="text-[var(--muted)]">Phone:</span> {e.phone || "—"}</p>
                  <p><span className="text-[var(--muted)]">Business:</span> {e.business_type || "—"}</p>
                  <p><span className="text-[var(--muted)]">Location:</span> {e.location || "—"}</p>
                  <p><span className="text-[var(--muted)]">Interests:</span> {e.interests?.length ? e.interests.join(", ") : "—"}</p>
                  <p className="pt-2 whitespace-pre-wrap">{e.message || <em className="text-[var(--muted)]">No message</em>}</p>
                </div>
                <div className="flex items-start justify-end gap-2">
                  {STATUS.map((s) => <button key={s} onClick={() => setStatus(e, s)} className={`chip !text-xs ${e.status === s ? "active" : ""}`} data-testid={`enquiry-status-${s}-${e.id}`}>{s}</button>)}
                  <button onClick={() => remove(e)} className="p-2 text-[var(--charcoal)] hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-[var(--muted)]">No enquiries yet.</p>}
      </div>
    </div>
  );
}
